import React, { useState, useEffect } from "react";
import { Tabs, Tab, Box, Typography, Alert } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdministrateLevels from "./AdministrateLevels";
import AdministrateRooms from "./AdministrateRooms";
import AdministrateModules from "./AdministrateModules";
import TemplateEditor from "./TemplateEditor";
import AdminExams from "./AdminExams";
import AdminLogs from "./AdminLogs";
import AdminStats from "./AdminStats";
import AdminUsers from "./AdminUsers";

const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const Admin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState(0);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is admin from localStorage
    const userData = localStorage.getItem("user");
    if (!userData) {
      navigate('/');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      const userRole = parsedUser.role ? parsedUser.role.toLowerCase() : '';
      
      // Check if user is admin or exam manager
      if (!parsedUser.role || (userRole !== 'admin' && userRole !== 'exam_manager')) {
        navigate('/');
        return;
      }

      // Set initial tab from URL params
      const tab = searchParams.get('tab');
      
      // If user is Exam Manager, force them to rooms tab
      if (userRole === 'exam_manager') {
        if (tab !== 'rooms') {
          setSearchParams({ tab: 'rooms' });
        }
        setValue(0); // For Exam Manager, Rooms is the only tab at index 0
      } else if (tab) {
        const tabIndex = getTabIndex(tab);
        if (tabIndex !== -1) {
          setValue(tabIndex);
        }
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
      navigate('/');
      return;
    }
  }, [searchParams, navigate]);

  const getTabIndex = (tabName) => {
    const tabMap = {
      'levels': 0,
      'rooms': 1,
      'modules': 2,
      'template': 3,
      'exams': 4,
      'logs': 5,
      'stats': 6,
      'users': 7
    };
    return tabMap[tabName] || 0;
  };

  const handleChange = (event, newValue) => {
    console.log('Tab clicked:', newValue, 'User role:', user?.role);
    
    // Prevent Exam Manager from changing tabs (they only have one tab)
    const userRole = user?.role ? user.role.toLowerCase() : '';
    if (userRole === 'exam_manager') {
      console.log('Exam Manager - blocking tab change');
      return; // Exam Manager only has Rooms tab
    }
    
    console.log('Admin - allowing tab change to:', newValue);
    setValue(newValue);
    
    // Update URL params
    const tabNames = ['levels', 'rooms', 'modules', 'template', 'exams', 'logs', 'stats', 'users'];
    const tabName = tabNames[newValue];
    if (tabName) {
      setSearchParams({ tab: tabName });
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  const userRole = user.role ? user.role.toLowerCase() : '';
  const isExamManager = userRole === 'exam_manager';
  const isAdmin = userRole === 'admin';
  
  if (!isAdmin && !isExamManager) {
    return (
      <Alert severity="error">
        Access denied. Admin privileges required.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "1200px", margin: "0 auto", padding: "2rem" }}>
      <Typography 
        variant="h3" 
        sx={{ 
          mb: 3, 
          textAlign: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontWeight: 700,
          textShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}
      >
        Administration Panel
      </Typography>

      {/* Debug section */}
      <Box sx={{ 
        mb: 3, 
        p: 2.5, 
        border: '1px solid #e0e0e0', 
        borderRadius: 2,
        backgroundColor: '#fafafa',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <Typography variant="h6" sx={{ mb: 1.5, color: '#333', fontWeight: 600 }}>
          Debug Info
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ 
            backgroundColor: '#e3f2fd', 
            padding: '0.5rem 1rem', 
            borderRadius: 1,
            fontSize: '0.9rem',
            color: '#1976d2'
          }}>
            User: {user?.email}
          </Typography>
          <Typography sx={{ 
            backgroundColor: '#f3e5f5', 
            padding: '0.5rem 1rem', 
            borderRadius: 1,
            fontSize: '0.9rem',
            color: '#7b1fa2'
          }}>
            Role: {user?.role}
          </Typography>
          <Typography sx={{ 
            backgroundColor: '#e8f5e8', 
            padding: '0.5rem 1rem', 
            borderRadius: 1,
            fontSize: '0.9rem',
            color: '#388e3c'
          }}>
            Is Admin: {user?.role && user.role.toLowerCase() === 'admin' ? 'Yes' : 'No'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ 
        backgroundColor: 'white', 
        borderRadius: 2, 
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
          sx={{
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e0e0e0',
            pointerEvents: 'auto',
            '& .MuiTab-root': {
              minHeight: '64px',
              fontSize: '0.95rem',
              fontWeight: 500,
              textTransform: 'none',
              color: '#666',
              cursor: 'pointer',
              pointerEvents: 'auto',
              '&.Mui-selected': {
                color: '#667eea',
                fontWeight: 600
              },
              '&:hover': {
                backgroundColor: '#f0f0f0'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#667eea',
              height: '3px'
            }
          }}
        >
          {isExamManager ? [
            <Tab key="rooms" label="Rooms" />
          ] : [
            <Tab key="levels" label="Levels" />,
            <Tab key="rooms" label="Rooms" />,
            <Tab key="modules" label="Modules" />,
            <Tab key="template" label="Template Editor" />,
            <Tab key="exams" label="Exams" />,
            <Tab key="logs" label="Logs" />,
            <Tab key="stats" label="Statistics" />,
            <Tab key="users" label="Users" />
          ]}
        </Tabs>

        <Box sx={{ minHeight: '400px' }}>
          {isExamManager ? (
            <TabPanel value={value} index={0}>
              <AdministrateRooms />
            </TabPanel>
          ) : (
            <>
              <TabPanel value={value} index={0}>
                <AdministrateLevels />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <AdministrateRooms />
              </TabPanel>
              <TabPanel value={value} index={2}>
                <AdministrateModules />
              </TabPanel>
              <TabPanel value={value} index={3}>
                <TemplateEditor />
              </TabPanel>
              <TabPanel value={value} index={4}>
                <AdminExams />
              </TabPanel>
              <TabPanel value={value} index={5}>
                <AdminLogs />
              </TabPanel>
              <TabPanel value={value} index={6}>
                <AdminStats />
              </TabPanel>
              <TabPanel value={value} index={7}>
                <AdminUsers />
              </TabPanel>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Admin;
